import React, { useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Skeleton,
  Typography,
} from "@mui/material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import "../studio-dialog.css";

interface RefinementResultDialogProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  activating?: boolean;
  currentBlocks: any[] | null;
  newBlocks: any[] | null;
  onSelectVersion: (useNew: boolean) => void;
  refinementLabels?: { label: string; group: string }[];
  topicName?: string;
}

interface DiffSegment {
  text: string;
  type: "same" | "added" | "removed";
}

const BLOCK_FIELDS: { key: "title" | "narration" | "caption"; label: string }[] = [
  { key: "title", label: "Title" },
  { key: "narration", label: "Narration" },
  { key: "caption", label: "Caption" },
];

function tokenize(text: string): string[] {
  return text.split(/(\s+)/);
}

function buildLcsTable(a: string[], b: string[]): number[][] {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] =
        a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1] + 1
          : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  return dp;
}

function diffWords(original: string, refined: string): DiffSegment[] {
  const a = tokenize(original);
  const b = tokenize(refined);

  if (a.length === 0 && b.length === 0) return [];

  const dp = buildLcsTable(a, b);
  const segments: DiffSegment[] = [];
  let i = a.length;
  let j = b.length;

  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      segments.push({ text: a[i - 1], type: "same" });
      i--;
      j--;
    } else if (dp[i - 1][j] > dp[i][j - 1]) {
      segments.push({ text: a[i - 1], type: "removed" });
      i--;
    } else {
      segments.push({ text: b[j - 1], type: "added" });
      j--;
    }
  }

  while (i > 0) {
    segments.push({ text: a[i - 1], type: "removed" });
    i--;
  }

  while (j > 0) {
    segments.push({ text: b[j - 1], type: "added" });
    j--;
  }

  segments.reverse();
  return segments;
}

const DiffText: React.FC<{
  segments: DiffSegment[];
  side: "original" | "refined";
  color: string;
}> = ({ segments, side, color }) => (
  <Typography
    variant="body2"
    sx={{ fontSize: 13, lineHeight: 1.5, color, wordBreak: "break-word" }}
  >
    {segments.map((seg, i) => {
      if (side === "original" && seg.type === "added") return null;
      if (side === "refined" && seg.type === "removed") return null;

      if (side === "original") {
        return <span key={i}>{seg.text}</span>;
      }

      if (seg.type === "added") {
        return (
          <span key={i} style={{ color: "#0f172a", fontWeight: 700 }}>
            {seg.text}
          </span>
        );
      }

      return <span key={i}>{seg.text}</span>;
    })}
  </Typography>
);

const LoadingSkeleton: React.FC = () => (
  <Box>
    <Box sx={{ display: "flex", gap: 1, mb: 2.5 }}>
      <Skeleton variant="rounded" width={100} height={26} sx={{ borderRadius: 1.5, bgcolor: "rgba(35, 100, 232, 0.08)" }} animation="wave" />
      <Skeleton variant="rounded" width={130} height={26} sx={{ borderRadius: 1.5, bgcolor: "rgba(35, 100, 232, 0.08)" }} animation="wave" />
    </Box>
    <Box className="ce-dialog-compare">
      <Box sx={{ display: "flex" }}>
        <Box sx={{ flex: 1, px: 3, py: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
          <Skeleton variant="circular" width={16} height={16} animation="wave" />
          <Skeleton variant="text" width={100} height={14} animation="wave" />
        </Box>
        <div className="ce-dialog-compare-divider" />
        <Box sx={{ flex: 1, px: 3, py: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
          <Skeleton variant="circular" width={15} height={15} animation="wave" />
          <Skeleton variant="text" width={100} height={14} animation="wave" />
        </Box>
      </Box>
      {[1, 2, 3].map((i) => (
        <Box key={i} sx={{ borderTop: "1px solid #eef2f8" }}>
          <Box sx={{ display: "flex" }}>
            <Box sx={{ flex: 1, px: 3, py: 2.5 }}>
              <Skeleton variant="text" width={50} height={13} sx={{ mb: 0.75 }} animation="wave" />
              <Skeleton variant="rounded" width="100%" height={40} sx={{ borderRadius: 1 }} animation="wave" />
            </Box>
            <div className="ce-dialog-compare-divider" />
            <Box sx={{ flex: 1, px: 3, py: 2.5 }}>
              <Skeleton variant="text" width={50} height={13} sx={{ mb: 0.75 }} animation="wave" />
              <Skeleton variant="rounded" width="100%" height={40} sx={{ borderRadius: 1 }} animation="wave" />
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
    <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
      <Skeleton variant="rounded" sx={{ flex: 1, height: 44, borderRadius: 2 }} animation="wave" />
      <Skeleton variant="rounded" sx={{ flex: 1, height: 44, borderRadius: 2 }} animation="wave" />
    </Box>
  </Box>
);

const RefinementResultDialog: React.FC<RefinementResultDialogProps> = ({
  open,
  onClose,
  loading,
  currentBlocks,
  newBlocks,
  onSelectVersion,
  refinementLabels,
  activating,
  topicName,
}) => {
  useEffect(() => {
    if (open) {
      const bodyOverflow = document.body.style.overflow;
      const htmlOverflow = document.documentElement.style.overflow;

      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";

      return () => {
        document.body.style.overflow = bodyOverflow;
        document.documentElement.style.overflow = htmlOverflow;
      };
    }
  }, [open]);

  return (
    <Dialog
      className="ce-studio-dialog"
      open={open}
      onClose={loading || activating ? undefined : onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        backdrop: { className: "ce-studio-dialog-backdrop" },
        paper: { className: "ce-studio-dialog-paper" },
      }}
      disableEscapeKeyDown={loading}
    >
      <div className="ce-dialog-header">
        <div className="ce-dialog-header-inner">
          <div className="ce-dialog-header-left">
            <div className="ce-dialog-icon-wrap">
              <AutoAwesomeIcon sx={{ fontSize: 24 }} />
            </div>
            <div>
              <h2 className="ce-dialog-title">Review AI Refinement</h2>
              <span className="ce-dialog-subtitle">
                {loading
                  ? "Refining your creative..."
                  : "Compare the original and refined versions, then choose which one to continue with"}
              </span>
            </div>
          </div>
          <IconButton
            className="ce-dialog-close"
            onClick={onClose}
            size="small"
            disabled={loading}
            aria-label="Close refinement review"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </div>
      </div>

      <DialogContent className="ce-dialog-body" sx={{ overflowY: "auto" }}>
        {loading ? (
          <LoadingSkeleton />
        ) : (
          <>
            {topicName && (
              <p className="ce-dialog-topic">{topicName}</p>
            )}
            {refinementLabels && refinementLabels.length > 0 && (
              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#4b5c75", mb: 1.5 }}>
                  Applied refinements
                </Typography>
                {(() => {
                  const groups = new Map<string, typeof refinementLabels>();
                  for (const item of refinementLabels) {
                    const g = groups.get(item.group) || [];
                    g.push(item);
                    groups.set(item.group, g);
                  }
                  return Array.from(groups.entries()).map(([group, items]) => (
                    <Box key={group} sx={{ mb: 1.5 }}>
                      <p className="ce-dialog-refine-group-label">{group}</p>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {items.map((item, i) => (
                          <span
                            key={i}
                            className={`ce-dialog-refine-chip${group === "Manual" ? " manual" : ""}`}
                          >
                            {item.label}
                          </span>
                        ))}
                      </Box>
                    </Box>
                  ));
                })()}
              </Box>
            )}
            <div className="ce-dialog-compare">
            <div className="ce-dialog-compare-header">
              <div className="ce-dialog-compare-col">
                <ArticleOutlinedIcon sx={{ fontSize: 16, color: "#4b5c75" }} />
                Original Version
              </div>
              <div className="ce-dialog-compare-divider" />
              <div className="ce-dialog-compare-col refined">
                <AutoAwesomeIcon sx={{ fontSize: 15, color: "#2364e8" }} />
                Refined Version
              </div>
            </div>
            {(currentBlocks || []).map((block: any, bi: number) => {
              const newBlock = (newBlocks || [])[bi] || {};
              const hasChanges = BLOCK_FIELDS.some((f) => (block[f.key] || "") !== (newBlock[f.key] || ""));
              return (
                <div key={block.id || bi} className={`ce-dialog-compare-block${hasChanges ? " changed" : ""}`}>
                  <div className="ce-dialog-compare-block-header">
                    <span className="ce-dialog-compare-block-label">
                      {block.block_type.replace(/_/g, " ")}
                    </span>
                  </div>
                  {BLOCK_FIELDS.map((f) => {
                    const curVal = (block[f.key] || "");
                    const newVal = (newBlock[f.key] || "");
                    if (!curVal && !newVal) return null;
                    const segments = diffWords(curVal, newVal);
                    return (
                      <div key={f.key} className="ce-dialog-compare-field">
                        <div className="ce-dialog-compare-field-inner">
                          <div className="ce-dialog-compare-field-col">
                            <div className="ce-dialog-compare-field-name">
                              {f.label}
                              <span className="ce-dialog-compare-side-badge side-original">Original</span>
                            </div>
                            <div className="ce-dialog-compare-field-text">
                              {segments.map((seg, i) => {
                                if (seg.type === "added") return null;
                                return <span key={i}>{seg.text}</span>;
                              })}
                            </div>
                          </div>
                          <div className="ce-dialog-compare-divider" />
                          <div className="ce-dialog-compare-field-col">
                            <div className="ce-dialog-compare-field-name">{f.label}</div>
                            <div className="ce-dialog-compare-field-text">
                              {segments.map((seg, i) => {
                                if (seg.type === "removed") return null;
                                if (seg.type === "added") {
                                  return <strong key={i}>{seg.text}</strong>;
                                }
                                return <span key={i}>{seg.text}</span>;
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
          </>
        )}
      </DialogContent>

      {!loading && (
        <div className="ce-dialog-footer">
          <div className="ce-dialog-footer-grid">
            <Button
              className="ce-dialog-btn-cancel"
              variant="outlined"
              onClick={() => onSelectVersion(false)}
              fullWidth
              disabled={activating}
            >
              Keep Current
            </Button>
            <div>
              <Button
                className="ce-dialog-btn-primary"
                variant="contained"
                onClick={() => onSelectVersion(true)}
                fullWidth
                disabled={activating}
                startIcon={activating ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <AutoAwesomeIcon sx={{ fontSize: 17 }} />}
              >
                Use Refined Version
              </Button>
              <p className="ce-dialog-footer-note">You can switch back to the original later</p>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default RefinementResultDialog;
